import React, { useCallback, useState } from 'react';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { DepotsHeader, DepotsTable, DepotsFilter, UpdateDepotStatusModal } from './';
import { useDepotsListagem, useDepotsNavegacao } from '../hooks';
import AnexarReciboModal from './AnexarReciboModal';
import { formatDepotStatusToSelect } from '../../config/statusOptions';

const DepotsCard = ({
  showActions = true,
  enableRowClick = true,
  showFilter = true,
  showHeader = true,
}) => {
  const [anexoModalOpen, setAnexoModalOpen] = useState(false);
  const [selectedDepotId, setSelectedDepotId] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const {
    data,
    loading,
    filterLoading,
    totalRegistros,
    resumoStatusPendentes,
    filtros,
    paginationState,
    handlePaginationChange,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handleUpdateStatus,
  } = useDepotsListagem();
  const { handleRowClick, handleAdicionar } = useDepotsNavegacao();

  const handleOpenAnexo = useCallback((id) => {
    if (!id) return;
    setSelectedDepotId(id);
    setAnexoModalOpen(true);
  }, []);

  const handleCloseAnexo = useCallback(() => {
    setAnexoModalOpen(false);
    setSelectedDepotId(null);
  }, []);

  const handleOpenStatusModal = useCallback(
    (id) => {
      if (!id) return;
      const row = data.find((item) => item.id === id);
      if (!row) return;

      setPendingStatusChange({
        id,
        numeroContainer: row.numeroContainer,
        selectedStatus: formatDepotStatusToSelect(row.status),
      });
      setStatusModalOpen(true);
    },
    [data],
  );

  const handleCloseStatusModal = useCallback(() => {
    if (statusUpdating) return;
    setStatusModalOpen(false);
    setPendingStatusChange(null);
  }, [statusUpdating]);

  const handleConfirmStatusChange = useCallback(async () => {
    if (!pendingStatusChange?.id || !pendingStatusChange?.selectedStatus?.value) return;
    try {
      setStatusUpdating(true);
      await handleUpdateStatus(pendingStatusChange.id, pendingStatusChange.selectedStatus.value);
    } finally {
      setStatusUpdating(false);
      setStatusModalOpen(false);
      setPendingStatusChange(null);
    }
  }, [handleUpdateStatus, pendingStatusChange]);

  const handleStatusSelectionChange = useCallback((selectedOption) => {
    setPendingStatusChange((prev) =>
      prev
        ? {
            ...prev,
            selectedStatus: selectedOption,
          }
        : prev,
    );
  }, []);

  return (
    <Card>
      <CardBody>
        {showHeader && (
          <DepotsHeader
            totalRegistros={totalRegistros}
            onAdicionar={handleAdicionar}
            statusValue={filtros?.Status}
            onStatusChange={(value) => handleFilterApply({ Status: value })}
            statusCounts={resumoStatusPendentes}
          />
        )}

        {showFilter && (
          <DepotsFilter
            loading={filterLoading}
            onApply={handleFilterApply}
            onLimpar={handleLimparFiltros}
          />
        )}

        <DepotsTable
          data={data}
          loading={loading}
          totalRegistros={totalRegistros}
          paginationState={paginationState}
          setPaginationState={handlePaginationChange}
          onRowClick={enableRowClick ? handleRowClick : undefined}
          onEditar={showActions ? handleOpenAnexo : undefined}
          onExcluir={showActions ? handleExcluir : undefined}
          onStatusClick={showActions ? handleOpenStatusModal : undefined}
        />
      </CardBody>
      <AnexarReciboModal
        show={anexoModalOpen}
        onClose={handleCloseAnexo}
        depotId={selectedDepotId}
      />
      <UpdateDepotStatusModal
        show={statusModalOpen}
        onClose={handleCloseStatusModal}
        onConfirm={handleConfirmStatusChange}
        onStatusChange={handleStatusSelectionChange}
        selectedStatus={pendingStatusChange?.selectedStatus || null}
        containerNumber={pendingStatusChange?.numeroContainer || ''}
        loading={statusUpdating}
      />
    </Card>
  );
};

export default DepotsCard;
